'use client'

import { ProjectCard } from '@/app/discover/components/project-card'
import { ArtistDetailFragment } from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { OverlineHeader } from '@components/overline-header'
import { BodyText, DetailText, DisplayText, HeadlineText } from 'design-configuration/components'
import React, { Suspense } from 'react'
import { SimilarArtists } from '../../editorial-artist/similar-artists'
import { FALLBACK_PROFILE_PIC } from '@consts/common'

type ArtistDisplayProps = {
  artist?: ArtistDetailFragment
}

export const ArtistDisplay = ({ artist }: ArtistDisplayProps): React.ReactElement => {
  const tags = artist?.projects?.map((project) => project.tags?.map((tag) => tag.tag_name)).flat() ?? []
  const avatar =
    artist?.user?.profile_by_id?.profile_picture?.url ??
    artist?.projects?.[0]?.featured_token?.[0]?.media_url ??
    FALLBACK_PROFILE_PIC

  return (
    <div>
      {/* header */}
      <div className="mt-nav-bar-height relative min-h-[350px] w-full">
        <ImageWithCustomLoader
          src={artist?.projects?.[0]?.tokens?.[0]?.media_url || ''}
          style={{
            objectFit: 'cover',
            objectPosition: 'center'
          }}
          fill
          priority
          blurHash={artist?.projects?.[0]?.tokens?.[0]?.image?.metadata?.blurHash || ''}
          alt={`Header photo for artist: ${artist?.user?.display_name}`}
          sizes="100vw"
          className="absolute inset-0 z-0"
        />
        <div className="py-2xl px-md relative z-10 flex h-full items-center">
          <div className="bg-surface-inverted gap-x-2xl rounded-xs p-2xl flex h-full w-full shadow-[3px_3px_6px_0px_rgba(0,0,0,0.2)]">
            <div className="gap-x-2xl gap-y-md flex flex-col items-center md:flex-row">
              <div className="relative h-[160px] w-[160px] flex-shrink-0">
                <ImageWithCustomLoader
                  src={avatar}
                  alt={`${artist?.user?.display_name}'s profile picture`}
                  className="rounded-full"
                  fill
                  style={{ objectFit: 'cover' }}
                />
              </div>
              <div className="text-on-surface-inverted flex h-full w-full flex-col items-center justify-center text-center md:items-start md:text-left">
                <DetailText className="text-on-surface-variant font-medium">ARTIST</DetailText>
                <HeadlineText className="py-xs">{artist?.user?.display_name ?? ''}</HeadlineText>
                <BodyText>{artist?.user?.profile_by_id?.bio}</BodyText>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* projects section */}
      <div className="py-2xl px-md">
        <OverlineHeader text="RELEASES" />
        <DisplayText>Art Blocks collections</DisplayText>

        <div
          className={cn(
            `py-md gap-md relative grid h-fit grid-flow-row auto-rows-fr grid-cols-1 sm:grid-cols-2 md:grid-cols-3`
          )}
        >
          {artist?.projects?.map((project) => <ProjectCard project={project} key={project.id} />)}
        </div>
      </div>
      <Suspense>
        <SimilarArtists tags={tags} artistAddress={artist?.public_address} />
      </Suspense>
    </div>
  )
}
